// Operating hours computation. Used for the dancer-facing "we're closed" view
// AND the late-room "practice mode" gating.
//
// Two schedules — each room has its own hours (America/Denver timezone):
//
//   MAIN ROOM:
//     • Tuesday   8:30pm – 12:00am (midnight)
//     • Thursday  8:30pm – 12:00am (midnight)
//     • Friday    8:30pm – 1:00am (next day, Saturday)
//     • Saturday  8:30pm – 1:00am (next day, Sunday)
//
//   LATE NIGHT LINE ROOM:
//     • Friday    11:30pm – 12:40am (next day, Saturday)
//     • Saturday  11:30pm – 12:40am (next day, Sunday)
//
//   PRACTICE MODE (late room only, Fri/Sat 8:30pm – 11:30pm):
//     The room is physically open and the instructor plays swing music for
//     warm-up, but the request system is NOT active. Dancers scanning the
//     late-room QR see a "practice mode" view; the instructor's view shows
//     a banner instead of the request management panel.

const TIMEZONE = 'America/Denver'

// Times below are stored as MINUTES SINCE MIDNIGHT, so 8:30pm = 20*60+30 = 1230.
// `end` may exceed 1440 (24*60) to indicate spillover into the next day —
// e.g. Friday 1500 means "closes at 25:00 = 1am Saturday".
const t = (h, m = 0) => h * 60 + m

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Day index: 0 = Sun, 1 = Mon, …, 6 = Sat
export const SCHEDULES = {
  main: {
    2: { start: t(20, 30), end: t(24),    label: 'Tuesday'  },
    4: { start: t(20, 30), end: t(24),    label: 'Thursday' },
    5: { start: t(20, 30), end: t(25),    label: 'Friday'   }, // closes 1am Sat
    6: { start: t(20, 30), end: t(25),    label: 'Saturday' }, // closes 1am Sun
  },
  late: {
    5: { start: t(23, 30), end: t(24, 40), label: 'Friday'   }, // closes 12:40am Sat
    6: { start: t(23, 30), end: t(24, 40), label: 'Saturday' }, // closes 12:40am Sun
  },
}

/** Format a minutes-since-midnight value as "8:30pm" / "9pm" / "1am". */
export function formatTime(minutes) {
  const h24 = Math.floor(minutes / 60) % 24
  const m = minutes % 60
  const period = h24 >= 12 ? 'pm' : 'am'
  let h = h24 % 12
  if (h === 0) h = 12
  return m === 0 ? `${h}${period}` : `${h}:${String(m).padStart(2, '0')}${period}`
}

/** Get the current weekday/hour/minute in America/Denver. */
export function nowInDenver(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value]),
  )
  const dayIndex = DAY_NAMES_SHORT.indexOf(parts.weekday)
  return {
    dayIndex,
    hour: parseInt(parts.hour, 10),
    minute: parseInt(parts.minute, 10),
  }
}

/**
 * True if `date` is within `schedule`'s operating hours.
 * Generic helper — pass SCHEDULES.main or SCHEDULES.late.
 */
export function isWithinSchedule(schedule, date = new Date()) {
  const { dayIndex, hour, minute } = nowInDenver(date)
  const nowMin = hour * 60 + minute
  const today = schedule[dayIndex]
  if (today && nowMin >= today.start && nowMin < Math.min(today.end, 24 * 60)) return true
  const yesterdayIdx = (dayIndex + 6) % 7
  const yesterday = schedule[yesterdayIdx]
  if (yesterday && yesterday.end > 24 * 60 && nowMin < yesterday.end - 24 * 60) return true
  return false
}

/** Convenience: true if the *main* room is within scheduled hours. */
export function isWithinScheduledHours(date = new Date()) {
  return isWithinSchedule(SCHEDULES.main, date)
}

/** True if the late room is within its full request-system hours (11:30pm–12:40am Fri/Sat). */
export function isLateRoomLive(date = new Date()) {
  return isWithinSchedule(SCHEDULES.late, date)
}

/**
 * True if the late room is in PRACTICE MODE — i.e., the main room is open but
 * the late room hasn't started its request system yet (Fri/Sat 8:30pm–11:30pm).
 * Outside of those windows, the late room is fully closed.
 */
export function isLateRoomInPractice(date = new Date()) {
  return isWithinScheduledHours(date) && !isLateRoomLive(date)
}

/**
 * Returns a friendly human-readable description of when MAIN next opens.
 * Used by the dancer ClosedScreen.
 */
export function nextOpenDescription(date = new Date()) {
  if (isWithinScheduledHours(date)) return ''
  const { dayIndex, hour, minute } = nowInDenver(date)
  const nowMin = hour * 60 + minute

  const today = SCHEDULES.main[dayIndex]
  if (today && nowMin < today.start) {
    return `Opens today at ${formatTime(today.start)}`
  }

  for (let i = 1; i <= 7; i++) {
    const futureIdx = (dayIndex + i) % 7
    const future = SCHEDULES.main[futureIdx]
    if (!future) continue
    if (i === 1) return `Opens tomorrow (${future.label}) at ${formatTime(future.start)}`
    return `Opens ${future.label} at ${formatTime(future.start)}`
  }
  return ''
}

/**
 * Returns a friendly description of when the late room opens its request system.
 * Used by the late-room practice-mode banner.
 */
export function nextLateLiveDescription(date = new Date()) {
  if (isLateRoomLive(date)) return ''
  const { dayIndex, hour, minute } = nowInDenver(date)
  const nowMin = hour * 60 + minute

  const today = SCHEDULES.late[dayIndex]
  if (today && nowMin < today.start) {
    return `Line dance opens at ${formatTime(today.start)}`
  }
  // Otherwise the late room isn't operating today; surface the next day it does.
  for (let i = 1; i <= 7; i++) {
    const futureIdx = (dayIndex + i) % 7
    const future = SCHEDULES.late[futureIdx]
    if (!future) continue
    if (i === 1) return `Line dance opens tomorrow (${future.label}) at ${formatTime(future.start)}`
    return `Line dance opens ${future.label} at ${formatTime(future.start)}`
  }
  return ''
}

/** Static schedule strings for display. */
export const SCHEDULE_LINES = [
  `Tue / Thu  ${formatTime(SCHEDULES.main[2].start)} – ${formatTime(SCHEDULES.main[2].end)}`,
  `Fri / Sat  ${formatTime(SCHEDULES.main[5].start)} – ${formatTime(SCHEDULES.main[5].end)}`,
]

export const LATE_SCHEDULE_LINE =
  `Room 2 — Fri / Sat ${formatTime(SCHEDULES.late[5].start)} – ${formatTime(SCHEDULES.late[5].end)}`

/** Sentence-form schedule string for the late room, used in dancer closed views. */
export const LATE_REQUESTS_OPEN_SENTENCE =
  `Line dance opens at ${formatTime(SCHEDULES.late[5].start)} on Fridays and Saturdays.`
